import { Injectable, effect, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { CompanyService } from './company.service';

/**
 * Keeps `CompanyService.activeCompany` in sync with the `:companySlug` route param.
 *
 * Eagerly instantiated from `AppComponent` so its router subscription is live for
 * every navigation — including the initial one on page reload. This is the single
 * place that mirrors URL → active company; feature components no longer need to
 * call `setActiveBySlug` themselves.
 *
 * Race handling: if a navigation lands on a slug before `companies()` has been
 * loaded, the slug is parked in `pendingSlug` and applied once companies arrive.
 */
@Injectable({ providedIn: 'root' })
export class CompanyUrlSyncService {
	private router = inject(Router);
	private companyService = inject(CompanyService);

	private pendingSlug = signal<string | null>(null);

	constructor() {
		this.router.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				takeUntilDestroyed(),
			)
			.subscribe(() => {
				const slug = this.findCompanySlug(this.router.routerState.snapshot.root);
				if (!slug) return;
				if (slug === this.companyService.activeCompany()?.slug) return;
				if (this.companyService.companies().length > 0) {
					this.companyService.setActiveBySlug(slug);
				} else {
					this.pendingSlug.set(slug);
				}
			});

		// Replay a pending slug once companies finish loading (race case).
		effect(() => {
			const hasCompanies = this.companyService.companies().length > 0;
			const pending = this.pendingSlug();
			if (hasCompanies && pending) {
				this.companyService.setActiveBySlug(pending);
				this.pendingSlug.set(null);
			}
		});
	}

	/** Walks the activated route tree and returns the deepest `companySlug` param, or null. */
	private findCompanySlug(route: ActivatedRouteSnapshot): string | null {
		let slug: string | null = (route.params['companySlug'] as string | undefined) ?? null;
		for (const child of route.children) {
			const childSlug = this.findCompanySlug(child);
			if (childSlug) slug = childSlug;
		}
		return slug;
	}
}